import React, { useState } from 'react'
import { useFormik } from 'formik'
import { t, Trans } from '@lingui/macro'
import {
  Box,
  ButtonClean,
  ButtonPrimary,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Modal
} from '../../../components'
import { brandColors } from '../../../../../shared/basicPalette'
import * as yup from 'yup'
import { fontSizes } from '../../../components/chakraThemeExtension'
import {
  useCustomSmtpQuery,
  usePatchCustomSmtpMutation
} from './CustomSMTP.codegen'

const SMTPDetailsSchema = yup.object().shape({
  host: yup.string().required(),
  port: yup.number().required(),
  user: yup.string().required(),
  pass: yup.string().required()
})

export const SMTPServerDetailsModal = ({ reject, resolve }) => {
  const { data } = useCustomSmtpQuery()
  const [patchSMTP] = usePatchCustomSmtpMutation()
  const [isSaving, setIsSaving] = useState(false)

  const errorMessageDefaultState = {
    defaultError: '',
    hostError: '',
    portError: '',
    userPassError: ''
  }
  const [errorMessage, setErrorMessage] = useState<{
    defaultError: string
    hostError: string
    portError: string
    userPassError: string
  }>({ ...errorMessageDefaultState })
  const config =
    data?.admin?.organisation?.custom_smtp?.configuration ?? ({} as any)
  const formik = useFormik({
    initialValues: {
      host: config.host,
      port: config.port,
      user: config.user,
      pass: config.pass
    },
    validationSchema: SMTPDetailsSchema,
    isInitialValid: false,
    onSubmit: (formData) => saveServerDetails(formData),
    enableReinitialize: true
  })

  const saveServerDetails = async (formData) => {
    if (formData && data?.admin?.organisation?.custom_smtp) {
      setIsSaving(true)
      try {
        const { data: result } = await patchSMTP({
          variables: {
            input: {
              mailFrom: data.admin.organisation.custom_smtp.emailIdentity,
              configuration: {
                host: formData.host,
                port: formData.port,
                user: formData.user,
                pass: formData.pass
              }
            }
          }
        })

        if (
          formData &&
          result?.admin?.organisation?.setCustomSMTP?.__typename ===
            'CustomSMTPError' &&
          result?.admin?.organisation?.setCustomSMTP?.error
        ) {
          const error = result?.admin?.organisation?.setCustomSMTP?.error

          if (error === 'EDNS') {
            setErrorMessage({
              ...errorMessageDefaultState,
              hostError: t`Incorrect host address`
            })
          } else if (error === 'ESOCKET') {
            setErrorMessage({
              ...errorMessageDefaultState,
              portError: t`Incorrect port`
            })
          } else if (error === 'EAUTH') {
            setErrorMessage({
              ...errorMessageDefaultState,
              userPassError: t`User name and password combination is not valid. Please try again.`
            })
          } else {
            setErrorMessage({
              ...errorMessageDefaultState,
              defaultError: t`Could not connect. Please check your details.`
            })
          }
        } else {
          resolve()
        }
      } catch (err) {
        throw err
      } finally {
        setIsSaving(false)
      }
    } else {
      setErrorMessage({
        ...errorMessageDefaultState,
        defaultError: 'Something is wrong'
      })
    }
  }

  return (
    <Modal
      newDesign
      size="WIDE"
      header={t`SMTP server details`}
      controls={
        <Flex mt="16px" justify="space-between" align="center" w="100%">
          <Box color={brandColors.red.mid}>
            {errorMessage.defaultError ? errorMessage.defaultError : null}
          </Box>
          <Flex>
            <ButtonClean onClick={reject}>
              <Trans>Cancel</Trans>
            </ButtonClean>
            <ButtonPrimary
              isLoading={isSaving}
              disabled={!formik.isValid}
              onClick={() => {
                formik.submitForm()
              }}
            >
              <Trans>Save</Trans>
            </ButtonPrimary>
          </Flex>
        </Flex>
      }
      onHide={reject}
    >
      <Box color={brandColors.grey.mid} mt="-16px" mb="12px">
        <Trans>
          Update the server details and save to validate. Only validated server
          details can be used to activate the custom SMTP.
        </Trans>
      </Box>

      <HStack align="flex-start">
        <FormControl
          isInvalid={!!errorMessage.hostError}
          isDisabled={data?.admin?.organisation?.custom_smtp?.enabled}
        >
          <FormLabel mt={0}>
            <Trans>Host</Trans>
          </FormLabel>
          <Input
            variant="filled"
            name="host"
            placeholder="smtp.server.com"
            value={formik.values.host}
            onChange={formik.handleChange}
          />
          <FormErrorMessage>{errorMessage.hostError}</FormErrorMessage>
        </FormControl>
        <FormControl isInvalid={!!errorMessage.portError}>
          <FormLabel mt={0}>
            <Trans>Port</Trans>
          </FormLabel>
          <HStack>
            <Input
              variant="filled"
              name="port"
              placeholder="25"
              value={formik.values.port}
              onChange={formik.handleChange}
            />
            <FormErrorMessage>{errorMessage.portError}</FormErrorMessage>
          </HStack>
        </FormControl>
      </HStack>
      <HStack align="flex-start">
        <FormControl isInvalid={!!errorMessage.userPassError}>
          <FormLabel>
            <Trans>Username</Trans>
          </FormLabel>
          <Input
            variant="filled"
            name="user"
            autoComplete="off"
            value={formik.values.user}
            onChange={formik.handleChange}
          />
        </FormControl>
        <FormControl isInvalid={!!errorMessage.userPassError}>
          <FormLabel>
            <Trans>Password</Trans>
          </FormLabel>
          <HStack>
            <Input
              variant="filled"
              type="password"
              autoComplete="off"
              name="pass"
              value={formik.values.pass}
              onChange={formik.handleChange}
            />
          </HStack>
        </FormControl>
      </HStack>
      {errorMessage.userPassError && (
        <Box fontSize={fontSizes.sm} color={brandColors.red.mid} mt="4px">
          {errorMessage.userPassError}
        </Box>
      )}
    </Modal>
  )
}
